'use client';

import Image from 'next/image';
import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useTrackEventAmplitude } from '../../../../core/Tracking/Amplitude/Amplitude';
import { BrandHandMarkTwoLinesVariant4 } from '../../../../design-system/components/illustration/BrandHandMarkTwoLinesVariant4';
import { NaturalScrollCarousel } from '../../../../design-system/components/natural-scroll-carousel/NaturalScrollCarousel';
import { imageLoaderForId } from '../../../../domains/Layout/Image/URL/ImageURL';
import { HomePageDocument } from '../../../../resources/homePageDocument/domain/types/homePageDocument';
import { useTrackBlockViewed } from '../../../../shared/hooks/useTrackBlockViewed';

import style from './AgenciesSection.module.css';
import { AgencyCard } from './AgencyCard/AgencyCard';
import { AgencyVideoModal } from './AgencyVideoModal/AgencyVideoModal';

type AgenciesSectionProps = {
    agencies: HomePageDocument['agencies'];
};

export function AgenciesSection({ agencies }: AgenciesSectionProps) {
    const { formatMessage } = useIntl();
    const { trackEvent } = useTrackEventAmplitude();
    const sectionRef = useRef<HTMLDivElement>(null);

    const [selectedAgencyWithVideo, setSelectedAgencyWithVideo] = useState<
        AgenciesSectionProps['agencies'][number] | null
    >(null);

    const isModalOpen = selectedAgencyWithVideo !== null;

    useTrackBlockViewed(sectionRef, 'agencies');

    function handleVideoClick(agencyWithVideo: AgenciesSectionProps['agencies'][number]) {
        setSelectedAgencyWithVideo(agencyWithVideo);

        trackEvent((ampli, defaultProperties) => {
            ampli.videoPlayed({
                ...defaultProperties,
                block: 'agencies',
            });
        });
    }

    return (
        <section ref={sectionRef} className={style.section}>
            <Image
                src="1825177"
                alt=""
                fill
                loader={imageLoaderForId({ aspectRatio: 16 / 9 })}
                className={style.backgroundImage}
                sizes="100vw"
            />
            <div className="layout-with-centered-content">
                <div className="main-container">
                    <div className={style.container}>
                        <div className={style.header}>
                            <h2 className={style.title}>
                                {formatMessage(
                                    { id: 'global.component.agencyList.headline' },
                                    {
                                        underline: (chunks) => {
                                            return (
                                                <em
                                                    key={`underline-${String(chunks)}`}
                                                    className={style.underline}
                                                >
                                                    {chunks}
                                                    <BrandHandMarkTwoLinesVariant4
                                                        className={style.handMark}
                                                    />
                                                </em>
                                            );
                                        },
                                    },
                                )}
                            </h2>
                            <p className={style.description}>
                                {formatMessage({ id: 'page.home.agencyBlocSubtitle' })}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <NaturalScrollCarousel
                slidesGap="4px"
                scrollSnapAlign="center"
                trackingInformation={{
                    navClick: {
                        block: 'agencies',
                    },
                }}
            >
                {agencies.map((agency) => {
                    return (
                        <NaturalScrollCarousel.Slide key={agency.agencyVideo.url}>
                            <AgencyCard agency={agency} onClick={handleVideoClick} />
                        </NaturalScrollCarousel.Slide>
                    );
                })}
            </NaturalScrollCarousel>

            {isModalOpen ? (
                <AgencyVideoModal
                    isOpen={isModalOpen}
                    close={() => setSelectedAgencyWithVideo(null)}
                    agencyName={selectedAgencyWithVideo.agencyName}
                    video={selectedAgencyWithVideo.agencyVideo}
                />
            ) : null}
        </section>
    );
}
